import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const nice: ChauffeurPage = {
  slug: "nice",
  title: "VTC & private chauffeur in Nice",
  subtitle:
    "Private chauffeur in Nice: airport, Promenade des Anglais, congresses and Riviera links.",
  keywords: ["vtc nice", "private chauffeur nice", "nice airport chauffeur"],
  hero: {
    title: "VTC & private chauffeur in Nice",
    subtitle:
      "Nice Côte d'Azur airport, Old Town, the port and business travel.",
    keywords: ["VTC Nice", "Nice airport chauffeur", "Promenade chauffeur"],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Nice",
    badges: ["Terminals 1 & 2", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Nice",
    items: [
      {
        title: "Frictionless airport",
        description:
          "France's second-busiest airport: name-board welcome at Terminals 1 and 2, with flight tracking.",
      },
      {
        title: "Local know-how",
        description:
          "Promenade des Anglais, the Old Town lanes, the corniches and the port at rush hour.",
      },
      {
        title: "Congresses & business",
        description:
          "Acropolis, the exhibition centre, city-centre hotels: business-grade punctuality.",
      },
      {
        title: "Riviera links",
        description:
          "Regular Nice ↔ Monaco and Nice ↔ Cannes hops via the A8 or the corniches.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Nice",
    services: [
      {
        title: "Airport transfers",
        text: "Nice Côte d'Azur (T1/T2) to the centre, hotels or the wider Riviera, with flight tracking.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or by the day for meetings, shopping and dinners in Nice.",
      },
      {
        title: "Congresses & events",
        text: "Shuttles to the Acropolis and the convention centres, with group coordination.",
      },
      {
        title: "Cruise terminal",
        text: "Pick-up at the port of Nice for calls and embarkations, with luggage assistance.",
      },
      {
        title: "Nights out & returns",
        text: "Old-Town restaurants, casinos and events, with safe late-night returns.",
      },
    ],
  },
  locations: {
    heading: "Districts & pick-up points",
    items: [
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, business aviation.",
      },
      {
        name: "Promenade des Anglais",
        description: "Palaces and seafront hotels.",
      },
      {
        name: "Old Town (Vieux-Nice)",
        description: "Cours Saleya, pedestrian lanes, close access.",
      },
      {
        name: "Carré d'Or & Jean-Médecin",
        description: "Shopping district and central hotels.",
      },
      {
        name: "Port of Nice",
        description: "Lympia basin and cruise terminal.",
      },
      {
        name: "Cimiez & Acropolis",
        description: "Residential quarter and convention centre.",
      },
    ],
  },
  generic: {
    heading: "Nice, the crossroads of the Riviera",
    subheading: "Airport, centre and corniches connected by one chauffeur.",
    paragraphs: [
      "Nice packs an international airport, a dense centre and a cruise terminal into one city — all places where parking and traffic tighten in season. A private chauffeur absorbs that: drop-off as close as possible, waiting handled, no hunt for a space.",
      "Between the lower corniche, the middle corniche and the A8, the best route to Monaco, Èze or Cap-Ferrat depends on the hour and the traffic. Our chauffeurs decide live to keep your schedule.",
      "Whether it's a congress at the Acropolis or a day from the Promenade des Anglais to the Old Town, hourly hire keeps the same car on hand from one appointment to the next.",
    ],
    bullets: ["T1/T2 flight tracking", "Water & chargers", "Corniche routing"],
  },
  process: {
    heading: "Book a chauffeur in Nice",
    steps: [
      "Share your flight or your addresses (airport, hotel, congress, port).",
      "We set the pick-up time and terminal based on your airline.",
      "The chauffeur tracks the flight and adjusts if it is delayed.",
      "Direct transfer or hourly hire depending on your day's programme.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Nice"),
    {
      q: "Do you pick up at Terminals 1 and 2 of Nice airport?",
      a: "Yes. We greet you at both terminals and at business aviation, with a name board and live tracking of your flight number.",
    },
    {
      q: "How long does it take to reach Monaco from Nice?",
      a: "Around 30 to 45 minutes depending on the route (lower corniche or A8) and the traffic. The chauffeur picks the fastest road at departure.",
    },
    {
      q: "Can you handle a cruise call at the port of Nice?",
      a: "Yes, we pick up and drop off at the port of Nice for embarkations and disembarkations, with luggage help and coordination around your call times.",
    },
    {
      q: "Is the centre of Nice reachable by car during tourist peaks?",
      a: "Parking is hard in season; the chauffeur drops you as close as possible to your address and stays reachable, so there is no need to look for a space.",
    },
  ],
  relatedLinks: [
    {
      label: "Cannes Nice Airport route",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Monaco Nice Airport route",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    { label: "Find a transfer", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "Your private chauffeur in Nice",
    text: "Airport, congress or Riviera link: book your ride in Nice.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title:
      "Car service Nice — private chauffeur, airport & Riviera | KS Rent Services",
    description:
      "Private car service in Nice: Côte d'Azur airport (T1/T2), congresses, Promenade des Anglais and Riviera links. Flight tracking, 24/7. Book online.",
    canonical: "/en/chauffeur/nice",
  },
};

export default nice;
